import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { useIntlContext } from 'context/TranslationContext';
import {
  createConversation,
  postMessage,
  uploadAttachment,
  attachmentUrl,
  fetchHistory,
} from 'components/SupportChat/api/supportApi';
import { useSupportStream } from 'components/SupportChat/hooks/useSupportStream';
import { SupportAttachment } from 'components/SupportChat/hooks/useSupportStream';

export type SupportSender = 'visitor' | 'operator' | 'system';

export interface SupportMessage {
  id?: number;
  localId: string;
  sender: SupportSender;
  body: string;
  // Optional file reference + the URL to render it (a server URL for incoming
  // messages, or a local objectURL preview for the visitor's own optimistic one).
  attachment?: SupportAttachment;
  attachmentSrc?: string;
}

const CONV_KEY = 'support_conversation_id';
const TOKEN_KEY = 'support_visitor_token';

const readStored = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStored = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage unavailable — conversation just won't persist across reloads */
  }
};

let localCounter = 0;
const nextLocalId = (): string => `l${Date.now()}_${localCounter++}`;

// A stored conversation can vanish server-side (expired, or deleted by an
// operator). The server then answers 404 — treat that as "start a fresh thread"
// rather than stranding the visitor on a dead conversation with "send failed".
const isConversationGone = (e: unknown): boolean =>
  (e as { response?: { status?: number } })?.response?.status === 404;

/**
 * Owns the support conversation: message list, visitor_token persistence
 * (localStorage, survives reloads — US2), and send flow. Identity is never
 * asserted by the client; the request is simply sent authenticated (Bearer
 * api-key) and the server derives who the user is.
 */
export const useSupportChat = () => {
  const userData = useSelector((state: RootState) => state.userData);
  const { locale } = useIntlContext();

  const token =
    userData?.apiKey || readStored('api_key') || undefined;

  const [conversationId, setConversationId] = useState<number | null>(() => {
    const raw = readStored(CONV_KEY);
    return raw ? Number(raw) : null;
  });
  const [visitorToken, setVisitorToken] = useState<string | undefined>(
    () => readStored(TOKEN_KEY) || undefined
  );
  // FOLLOW-UP (US2 SHOULD, partial): only conversation_id + visitor_token are
  // persisted, so on reload the thread starts empty — the visitor's own past
  // messages are not replayed (SSE replay carries operator/system only). Needs
  // a GET /support/conversations/{id}/messages history fetch on mount.
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seenIds = useRef<Set<number>>(new Set());

  const appendMessage = useCallback((msg: SupportMessage) => {
    setMessages((prev) => {
      if (msg.id != null && seenIds.current.has(msg.id)) return prev;
      if (msg.id != null) seenIds.current.add(msg.id);
      return [...prev, msg];
    });
  }, []);

  const persistConversation = useCallback((id: number, vtok: string) => {
    setConversationId(id);
    setVisitorToken(vtok);
    writeStored(CONV_KEY, String(id));
    writeStored(TOKEN_KEY, vtok);
  }, []);

  // Drop the stored conversation so the next send opens a fresh one. Used when
  // the server reports the conversation is gone (404) — see isConversationGone.
  const clearStoredConversation = useCallback(() => {
    setConversationId(null);
    setVisitorToken(undefined);
    try {
      localStorage.removeItem(CONV_KEY);
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* storage unavailable */
    }
  }, []);

  // Realtime operator/system messages -> append to the thread.
  useSupportStream({
    conversationId,
    visitorToken,
    token,
    onEvent: (evt) => {
      // An attachment-only message carries an empty body — don't drop it.
      if (!evt?.body && !evt?.attachment) return;
      appendMessage({
        id: evt.id,
        localId: evt.id != null ? `s${evt.id}` : nextLocalId(),
        sender: (evt.sender as SupportSender) || 'operator',
        body: evt.body || '',
        attachment: evt.attachment,
        attachmentSrc: evt.attachment
          ? attachmentUrl(evt.attachment.id, visitorToken, token)
          : undefined,
      });
    },
  });

  const sendMessage = useCallback(
    async (text: string) => {
      const body = text.trim();
      if (!body || sending) return;
      setSending(true);
      setError(null);

      // Optimistic echo of the visitor's own message.
      appendMessage({
        localId: nextLocalId(),
        sender: 'visitor',
        body,
      });

      try {
        if (conversationId == null) {
          const res = await createConversation({
            message: body,
            locale,
            visitorToken,
            token,
          });
          persistConversation(res.conversation_id, res.visitor_token);
        } else {
          try {
            await postMessage({
              conversationId,
              message: body,
              visitorToken,
              token,
            });
          } catch (e) {
            if (!isConversationGone(e)) throw e;
            // Stored conversation was removed server-side — open a fresh one and
            // send this message as its opening message (no stale visitor_token).
            clearStoredConversation();
            const res = await createConversation({ message: body, locale, token });
            persistConversation(res.conversation_id, res.visitor_token);
          }
        }
      } catch (e) {
        // Fail quietly (US1): keep the widget usable, surface a soft hint.
        setError('send_failed');
      } finally {
        setSending(false);
      }
    },
    [
      appendMessage,
      clearStoredConversation,
      conversationId,
      locale,
      persistConversation,
      sending,
      token,
      visitorToken,
    ]
  );

  const sendAttachment = useCallback(
    async (file: File, caption?: string) => {
      if (!file || sending) return;
      setSending(true);
      setError(null);

      const trimmedCaption = (caption || '').trim();
      const isImage = file.type.startsWith('image/');
      const localId = nextLocalId();
      // Optimistic echo with a local objectURL preview (images only).
      const previewUrl = isImage ? URL.createObjectURL(file) : undefined;
      appendMessage({
        localId,
        sender: 'visitor',
        body: trimmedCaption,
        attachment: {
          id: localId,
          kind: isImage ? 'image' : 'file',
          name: file.name,
          mime: file.type,
          size: file.size,
        },
        attachmentSrc: previewUrl,
      });

      try {
        // A file can be the very first interaction — open a conversation first
        // (its opening message requires text, so use the caption or the name).
        if (conversationId == null) {
          const res = await createConversation({
            message: trimmedCaption || `📎 ${file.name}`,
            locale,
            visitorToken,
            token,
          });
          persistConversation(res.conversation_id, res.visitor_token);
          // Caption already became the opening message — upload without it.
          await uploadAttachment({
            conversationId: res.conversation_id,
            file,
            visitorToken: res.visitor_token,
            token,
          });
        } else {
          try {
            await uploadAttachment({
              conversationId,
              file,
              caption: trimmedCaption || undefined,
              visitorToken,
              token,
            });
          } catch (e) {
            if (!isConversationGone(e)) throw e;
            // Stored conversation vanished — open a fresh one; the caption becomes
            // its opening message, then upload the file without a duplicate caption.
            clearStoredConversation();
            const res = await createConversation({
              message: trimmedCaption || `📎 ${file.name}`,
              locale,
              token,
            });
            persistConversation(res.conversation_id, res.visitor_token);
            await uploadAttachment({
              conversationId: res.conversation_id,
              file,
              visitorToken: res.visitor_token,
              token,
            });
          }
        }
      } catch (e) {
        setError('send_failed');
      } finally {
        setSending(false);
      }
    },
    [
      appendMessage,
      clearStoredConversation,
      conversationId,
      locale,
      persistConversation,
      sending,
      token,
      visitorToken,
    ]
  );

  // Rehydrate the thread from server history once on mount when a conversation
  // was restored from localStorage — the visitor's own past messages (and any
  // attachments) are only stored server-side (fixes "sent messages lost on
  // reload"). SSE replay then covers anything newer.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current || conversationId == null) return;
    hydratedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchHistory({ conversationId, visitorToken, token });
        if (cancelled || rows.length === 0) return;
        setMessages((prev) => {
          const existing = new Set(prev.map((m) => m.id).filter((v) => v != null));
          const hydrated = rows
            .filter((r) => !existing.has(r.id))
            .map((r) => {
              if (r.id != null) seenIds.current.add(r.id);
              return {
                id: r.id,
                localId: `h${r.id}`,
                sender: (r.sender as SupportSender) || 'operator',
                body: r.body || '',
                attachment: r.attachment,
                attachmentSrc: r.attachment
                  ? attachmentUrl(r.attachment.id, visitorToken, token)
                  : undefined,
              } as SupportMessage;
            });
          return [...hydrated, ...prev].sort(
            (a, b) => (a.id ?? Infinity) - (b.id ?? Infinity)
          );
        });
      } catch {
        /* history fetch is best-effort — a fresh thread just starts empty */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [conversationId, visitorToken, token]);

  // Reset the seen-id cache if the conversation id changes (new session).
  useEffect(() => {
    seenIds.current = new Set();
  }, [conversationId]);

  return {
    messages,
    sending,
    error,
    conversationId,
    isLoggedIn: !!userData?.isUserLoggedIn,
    sendMessage,
    sendAttachment,
  };
};
